import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Plus, Search, Package, Settings as SettingsIcon, Upload, Minimize2, Maximize2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { PageHeader, Card, PrimaryButton, Input, EmptyState, QuietButton } from "../components/UI";
import MaterialsSettingsTab from "./MaterialsSettingsTab";
import MaterialsImportsTab from "./MaterialsImportsTab";
import { groupMaterials, groupBySupplier, GroupHeader, MaterialRow } from "../components/materials/MaterialCategoryGroups";

const TABS = new Set(["list", "settings", "imports"]);
// Collapsible categories (13/09) — which groups are OPEN, per browser. Default
// (nothing stored) = all closed; a category added later starts closed too.
const OPEN_KEY = "lt_materials_open_groups_v1";
const readOpen = () => { try { return new Set(JSON.parse(localStorage.getItem(OPEN_KEY) || "[]")); } catch { return new Set(); } };
// Group by (15/09, Chris) — Category (the original) or Supplier → Category, so
// every Travis Perkins material collapses into one row. Remembered per browser.
const GROUP_BY_KEY = "lt_materials_group_by_v1";
const readGroupBy = () => (localStorage.getItem(GROUP_BY_KEY) === "supplier" ? "supplier" : "category");

export default function Materials() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const activeTab = TABS.has(params.get("tab")) ? params.get("tab") : "list";
  const setTab = (t) => setParams(t !== "list" ? { tab: t } : {});
  const [items, setItems] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [units, setUnits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [openGroups, setOpenGroups] = useState(readOpen);
  const persistOpen = (next) => { setOpenGroups(next); localStorage.setItem(OPEN_KEY, JSON.stringify(Array.from(next))); };
  const [groupBy, setGroupBy] = useState(readGroupBy);
  const persistGroupBy = (v) => { setGroupBy(v); localStorage.setItem(GROUP_BY_KEY, v); };
  // LT-18 v1 §2f — filter by preferred supplier + by active/archived
  // status. Supplier options come from the loaded suppliers list (which
  // includes archived, so a filter to "archived supplier X" still
  // works). Status has three values: all / active / archived, keyed off
  // Material.is_archived. "all" defers to the showArchived checkbox.
  const [supplierFilter, setSupplierFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const supplierById = React.useMemo(
    () => Object.fromEntries(suppliers.map((s) => [s.id, s])),
    [suppliers],
  );
  // LT-18 §11 close-out (2026-09-12) — resolve category KEY → LABEL.
  // Post-slug-unification, `m.category` holds a slug like `timber` or
  // `sheet_materials`. Rendering that verbatim would show the user
  // machine-shaped strings; look up the human label from the loaded
  // material_categories collection instead. Falls back to the raw
  // value when no row matches (defensive — a legacy material with an
  // orphaned category will still render its stored value rather than
  // vanish).
  const categoryLabelByKey = React.useMemo(
    () => Object.fromEntries(categories.map((c) => [c.key, c.label])),
    [categories],
  );
  const unitLabelByKey = React.useMemo(
    () => Object.fromEntries(units.map((u) => [u.key, u.label])),
    [units],
  );

  const load = () => {
    setLoading(true);
    // Status "archived" implies the archived rows must be in the source list
    // (the tester's quirk: the filter did nothing unless Show archived was ticked).
    const includeArchived = showArchived || statusFilter === "archived";
    api.get("/materials", { params: { include_archived: includeArchived, q: q || undefined } })
      .then((r) => setItems(r.data || []))
      .catch(() => toast.error("Failed to load materials"))
      .finally(() => setLoading(false));
  };
  useEffect(load, [showArchived, statusFilter]); // eslint-disable-line
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q]); // eslint-disable-line
  // LT-18 — pull suppliers once so the list can resolve preferred supplier
  // names against IDs. Includes archived so a material whose preferred
  // supplier was archived still shows its name.
  // LT-18 §11 close-out — also pull categories + units so the list
  // can render the human LABEL beside each material rather than the
  // slug KEY. Include archived so a material tagged against an
  // archived taxonomy still shows the correct label.
  useEffect(() => {
    api.get("/suppliers", { params: { include_archived: true } })
      .then((r) => setSuppliers(r.data || []))
      .catch(() => { /* non-fatal */ });
    api.get("/material-categories", { params: { include_archived: true } })
      .then((r) => setCategories(r.data || []))
      .catch(() => { /* non-fatal */ });
    api.get("/material-units", { params: { include_archived: true } })
      .then((r) => setUnits(r.data || []))
      .catch(() => { /* non-fatal */ });
  }, []);

  const filteredItems = React.useMemo(() => {
    let out = items;
    // LT-18 v1 §2f — supplier filter matches the material's PREFERRED
    // supplier only (falls back to the first link if none is preferred,
    // same rule the list column uses to render the supplier cell).
    if (supplierFilter) {
      out = out.filter((m) => {
        const links = m.supplier_links || [];
        const pref = links.find((l) => l.is_preferred) || links[0];
        return pref && pref.supplier_id === supplierFilter;
      });
    }
    if (statusFilter === "active") {
      out = out.filter((m) => !m.is_archived);
    } else if (statusFilter === "archived") {
      out = out.filter((m) => m.is_archived);
    } else if (!showArchived) {
      out = out.filter((m) => !m.is_archived);
    }
    return out;
  }, [items, supplierFilter, statusFilter, showArchived]);

  // Collapsible categories — Settings order, name A–Z inside. A search opens
  // every group that has a match and hides the rest; clearing it goes back
  // to what was open before.
  const groups = React.useMemo(
    () => (groupBy === "supplier" ? groupBySupplier(filteredItems, categories, supplierById) : groupMaterials(filteredItems, categories)),
    [filteredItems, categories, supplierById, groupBy],
  );
  const allKeys = React.useMemo(() => groups.flatMap((g) => [g.key, ...(g.children || []).map((c) => c.key)]), [groups]);
  const searching = q.trim().length > 0;
  const isOpen = (key) => searching || openGroups.has(key);
  const toggleGroup = (key) => {
    if (searching) return;
    const next = new Set(openGroups);
    if (next.has(key)) next.delete(key); else next.add(key);
    persistOpen(next);
  };
  const expandAll = () => persistOpen(new Set([...openGroups, ...allKeys]));
  const collapseAll = () => persistOpen(new Set([...openGroups].filter((k) => !allKeys.includes(k))));
  const allOpen = groups.length > 0 && allKeys.every((k) => openGroups.has(k));
  const renderRows = (g) => g.rows.map((m, i) => (
    <MaterialRow key={m.id} m={m} last={i === g.rows.length - 1}
      categoryLabel={categoryLabelByKey[m.category] || m.category}
      unitLabel={unitLabelByKey[m.unit] || m.unit}
      supplierById={supplierById}
      onOpen={() => navigate(`/materials/${m.id}`)} />
  ));

  return (
    <div className="app-container py-8">
      <PageHeader
        title="Materials"
        subtitle="Master material list — prices come from supplier links."
        actions={activeTab === "list" ? (
          <PrimaryButton icon={Plus} onClick={() => navigate("/materials/new")}>Add material</PrimaryButton>
        ) : null}
      />

      {/* LT-18 Phase 3 — tab bar */}
      <div className="mb-4 flex items-center gap-1 border-b border-line" data-testid="materials-tabbar">
        <button
          type="button"
          onClick={() => setTab("list")}
          className={"px-4 py-2 -mb-px border-b-2 " + (activeTab === "list"
            ? "border-accent text-ink font-medium"
            : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }}
          data-testid="materials-tab-list"
        >
          Materials
        </button>
        <button
          type="button"
          onClick={() => setTab("imports")}
          className={"px-4 py-2 -mb-px border-b-2 inline-flex items-center gap-1.5 " + (activeTab === "imports"
            ? "border-accent text-ink font-medium"
            : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }}
          data-testid="materials-tab-imports"
        >
          <Upload size={13} />
          Imports
        </button>
        <button
          type="button"
          onClick={() => setTab("settings")}
          className={"px-4 py-2 -mb-px border-b-2 inline-flex items-center gap-1.5 " + (activeTab === "settings"
            ? "border-accent text-ink font-medium"
            : "border-transparent text-ink-3 hover:text-ink")}
          style={{ fontSize: 13 }}
          data-testid="materials-tab-settings"
        >
          <SettingsIcon size={13} />
          Settings
        </button>
      </div>

      {activeTab === "settings" ? (
        <>
          <div className="mb-3 text-ink-4" style={{ fontSize: 11.5 }}>
            Also under <Link to="/settings?tab=library_materials" className="underline hover:text-ink">Settings → Library settings → Materials</Link> — edit from either place.
          </div>
          <MaterialsSettingsTab />
        </>
      ) : activeTab === "imports" ? (
        <MaterialsImportsTab />
      ) : (
        <>

      <div className="flex items-center justify-between mb-4 gap-3">
        <div className="flex items-center gap-2 flex-1 max-w-md">
          <div className="relative flex-1">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-ink-4" />
            <Input
              value={q} onChange={setQ}
              placeholder="Search by name, code or supplier…"
            />
            <style>{`input[placeholder*="Search"]{padding-left:32px}`}</style>
          </div>
          {suppliers.length > 0 && (
            <select
              value={supplierFilter}
              onChange={(e) => setSupplierFilter(e.target.value)}
              className="h-9 px-2.5 rounded-md border border-line bg-surface text-ink"
              style={{ fontSize: 12.5 }}
              data-testid="materials-supplier-filter"
            >
              <option value="">All suppliers</option>
              {suppliers
                .slice()
                .sort((a, b) => (a.name || "").localeCompare(b.name || ""))
                .map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}{s.status === "archived" ? " (archived)" : ""}
                  </option>
                ))}
            </select>
          )}
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="h-9 px-2.5 rounded-md border border-line bg-surface text-ink"
            style={{ fontSize: 12.5 }}
            data-testid="materials-status-filter"
          >
            <option value="all">All statuses</option>
            <option value="active">Active</option>
            <option value="archived">Archived</option>
          </select>
        </div>
        <div className="flex items-center gap-3">
          <div className="inline-flex items-center rounded-md border border-line overflow-hidden" data-testid="materials-group-by" role="group" aria-label="Group by">
            <span className="px-2 text-ink-4" style={{ fontSize: 11 }}>Group by</span>
            {[["category", "Category"], ["supplier", "Supplier"]].map(([v, l]) => (
              <button key={v} type="button" onClick={() => persistGroupBy(v)}
                className={"px-2.5 h-8 " + (groupBy === v ? "bg-ink text-white" : "text-ink-3 hover:text-ink hover:bg-surface-2")}
                style={{ fontSize: 12 }} aria-pressed={groupBy === v} data-testid={`materials-group-by-${v}`}>{l}</button>
            ))}
          </div>
          {groups.length > 0 && !searching && (
            allOpen
              ? <QuietButton icon={Minimize2} onClick={collapseAll} data-testid="materials-collapse-all">Collapse all</QuietButton>
              : <QuietButton icon={Maximize2} onClick={expandAll} data-testid="materials-expand-all">Expand all</QuietButton>
          )}
          <label className="inline-flex items-center gap-2 text-ink-3" style={{ fontSize: 12 }}>
            <input type="checkbox" checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)} data-testid="materials-show-archived" />
            Show archived
          </label>
        </div>
      </div>

      <Card>
        {loading ? (
          <div className="py-12 text-center text-ink-3" style={{ fontSize: 12 }}>Loading…</div>
        ) : items.length === 0 && !searching && !supplierFilter && statusFilter === "all" ? (
          <EmptyState
            icon={Package}
            title="No materials yet"
            hint="Add your first material to start building cost data."
            action={<PrimaryButton icon={Plus} onClick={() => navigate("/materials/new")}>Add material</PrimaryButton>}
          />
        ) : groups.length === 0 ? (
          <EmptyState icon={Search} title="Nothing matches" hint="No material matches the search and filters." />
        ) : (
          <table className="w-full text-left" data-testid="materials-table">
            <thead>
              <tr className="border-b border-line bg-surface-2/50">
                <th className="col-head px-4 py-2.5" style={{ width: 120 }}>Code</th>
                <th className="col-head px-4 py-2.5">Name</th>
                <th className="col-head px-4 py-2.5" style={{ width: 130 }}>Category</th>
                <th className="col-head px-4 py-2.5" style={{ width: 60 }}>Unit</th>
                <th className="col-head px-4 py-2.5 text-right" style={{ width: 120 }}>Current price</th>
                <th className="col-head px-4 py-2.5">Preferred supplier</th>
                <th className="col-head px-4 py-2.5" style={{ width: 90 }}>Updated</th>
                <th className="col-head px-4 py-2.5" style={{ width: 70 }}>Status</th>
                <th style={{ width: 30 }} />
              </tr>
            </thead>
            <tbody>
              {groups.map((g) => (
                <React.Fragment key={g.key}>
                  <GroupHeader group={g} open={isOpen(g.key)} onToggle={() => toggleGroup(g.key)} />
                  {isOpen(g.key) && (g.children
                    ? g.children.map((c) => (
                        <React.Fragment key={c.key}>
                          <GroupHeader group={c} open={isOpen(c.key)} onToggle={() => toggleGroup(c.key)} depth={1} />
                          {isOpen(c.key) && renderRows(c)}
                        </React.Fragment>
                      ))
                    : renderRows(g))}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </Card>
        </>
      )}
    </div>
  );
}
